/*
 * DetectionRules.tsx — S61: Custom Detection Rules
 * API: GET /api/v1/rules, POST /api/v1/rules, POST /api/v1/rules/test
 */
import { useState, useEffect } from 'react';
import { API_V1, authFetch, safeFetch } from '../constants';
import { ConditionGroupEditor, type ConditionGroup, isGroup } from '../components/rules/ConditionGroupEditor';
import { SeverityRulesEditor, type SeverityRule } from '../components/rules/SeverityRulesEditor';
import { RuleTemplateGallery, type RuleTemplate } from '../components/rules/RuleTemplateGallery';
import { EntityFrameworkScoping } from '../components/rules/EntityFrameworkScoping';
import { RuleAnalytics } from '../components/rules/RuleAnalytics';
import { VersionHistory } from '../components/rules/VersionHistory';

interface Rule {
  id: number;
  rule_name: string;
  rule_code: string;
  description: string;
  category: string;
  exception_type: string;
  severity: string;
  conditions: any;
  output_template: any;
  is_active: boolean;
  is_system_rule: boolean;
  priority: number;
  version: number;
  l_level_required: string;
  last_run_at: string | null;
  created_at: string | null;
}

interface RuleRun {
  id: number;
  rule_id: number;
  entity_id: number;
  ran_at: string;
  rows_scanned: number;
  exceptions_found: number;
  execution_time_ms: number;
  status: string;
  error_message: string | null;
}

// Local Condition / ConditionGroup types removed — using imports from ConditionGroupEditor

const SEVERITY_COLORS: Record<string, string> = {
  CRITICAL: 'var(--red)',
  HIGH: '#e65100',
  MEDIUM: 'var(--amber)',
  LOW: 'var(--green)',
};

const CATEGORIES = [
  'accounts_payable', 'accounts_receivable', 'general_ledger', 'intercompany',
  'treasury', 'payroll', 'procurement', 'financial_planning',
  'credit_management', 'systems', 'financial_reporting', 'custom',
];

const OPERATORS = [
  'GT', 'LT', 'GTE', 'LTE', 'EQ', 'NEQ',
  'IN', 'NOT_IN', 'CONTAINS', 'STARTS_WITH',
  'IS_NULL', 'IS_NOT_NULL', 'OLDER_THAN_DAYS', 'NEWER_THAN_DAYS',
];

const FIELDS = [
  'amount', 'abs_amount', 'aging_days', 'account_code', 'account_type',
  'vendor_code', 'customer_code', 'posting_date', 'entity_id', 'currency',
  'description', 'posted_by', 'approved_by', 'debit_credit', 'line_number',
  'document_number', 'cost_center', 'profit_center', 'project_code',
];

export default function DetectionRules() {
  const [rules, setRules] = useState<Rule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRule, setSelectedRule] = useState<(Rule & { recent_runs?: RuleRun[] }) | null>(null);
  const [showBuilder, setShowBuilder] = useState(false);
  const [testResult, setTestResult] = useState<any>(null);
  const [actionMsg, setActionMsg] = useState('');

  // Builder state
  const [formName, setFormName] = useState('');
  const [formCode, setFormCode] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formCategory, setFormCategory] = useState('general_ledger');
  const [formExcType, setFormExcType] = useState('journal_anomaly');
  const [formSeverity, setFormSeverity] = useState('MEDIUM');
  const [formPriority, setFormPriority] = useState(100);
  const [formConditionGroup, setFormConditionGroup] = useState<ConditionGroup>({
    logic: 'AND',
    rules: [{ field: 'amount', operator: 'GT', value: 0 }],
  });
  const [formSeverityRules, setFormSeverityRules] = useState<SeverityRule[]>([]);
  const [formTitle, setFormTitle] = useState('');
  const [formDescTemplate, setFormDescTemplate] = useState('');
  const [formAction, setFormAction] = useState('');
  const [formEntities, setFormEntities] = useState<string[]>([]);
  const [formFrameworks, setFormFrameworks] = useState<string[]>([]);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [availableEntities, setAvailableEntities] = useState<Array<{ code: string; name: string }>>([]);

  const load = () => {
    setLoading(true); setError('');
    safeFetch(`${API_V1}/rules`, { rules: [] })
      .then(data => {
        const r = data?.rules ?? data;
        setRules(Array.isArray(r) ? r : []);
        setLoading(false);
      })
      .catch(e => { setError(e.message); setLoading(false); });
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    safeFetch(`${API_V1}/entities`, [])
      .then((data: any) => {
        const list = Array.isArray(data) ? data : data?.entities || [];
        setAvailableEntities(list.map((e: any) => ({ code: e.entity_code || e.code || '', name: e.entity_name || e.name || '' })));
      })
      .catch(() => {});
  }, []);

  const loadRuleDetail = (id: number) => {
    authFetch(`${API_V1}/rules/${id}`)
      .then(r => r.json())
      .then(data => setSelectedRule(data))
      .catch(() => {});
  };

  const toggleActive = (rule: Rule) => {
    const endpoint = rule.is_active ? 'deactivate' : 'activate';
    authFetch(`${API_V1}/rules/${rule.id}/${endpoint}`, { method: 'PUT' })
      .then(r => { if (!r.ok) throw new Error(`${r.status}`); return r.json(); })
      .then(() => { setActionMsg(`Rule ${rule.is_active ? 'deactivated' : 'activated'}`); load(); })
      .catch(e => setActionMsg(`Error: ${e.message}`));
  };

  const runRule = (ruleId: number) => {
    authFetch(`${API_V1}/rules/${ruleId}/run`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ entity_id: 1000 }),
    })
      .then(r => r.json())
      .then(data => setActionMsg(data.message || 'Rule execution queued'))
      .catch(e => setActionMsg(`Error: ${e.message}`));
  };

  const testRuleDry = () => {
    setTestResult(null);
    authFetch(`${API_V1}/rules/test`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        conditions: formConditionGroup,
        entity_id: 1000,
        sample_size: 100,
      }),
    })
      .then(r => r.json())
      .then(data => setTestResult(data))
      .catch(e => setTestResult({ error: e.message }));
  };

  const saveRule = () => {
    const payload = {
      rule_name: formName,
      rule_code: formCode,
      description: formDesc,
      category: formCategory,
      exception_type: formExcType,
      severity: formSeverity,
      severity_rules: formSeverityRules.length > 0 ? formSeverityRules : undefined,
      conditions: formConditionGroup,
      applies_to_entities: formEntities.length > 0 ? formEntities : null,
      applies_to_frameworks: formFrameworks.length > 0 ? formFrameworks : null,
      output_template: {
        title: formTitle,
        description_template: formDescTemplate,
        recommended_action: formAction,
      },
      priority: formPriority,
    };

    const url = editingId ? `${API_V1}/rules/${editingId}` : `${API_V1}/rules`;
    const method = editingId ? 'PUT' : 'POST';

    authFetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(r => { if (!r.ok) throw new Error(`${r.status}`); return r.json(); })
      .then(() => { setShowBuilder(false); setEditingId(null); setActionMsg('Rule saved'); load(); })
      .catch(e => setActionMsg(`Error: ${e.message}`));
  };

  const openEdit = (rule: Rule) => {
    setEditingId(rule.id);
    setFormName(rule.rule_name);
    setFormCode(rule.rule_code);
    setFormDesc(rule.description || '');
    setFormCategory(rule.category);
    setFormExcType(rule.exception_type);
    setFormSeverity(rule.severity);
    setFormPriority(rule.priority);
    setFormConditionGroup({
      logic: rule.conditions?.logic || 'AND',
      rules: rule.conditions?.rules || [{ field: 'amount', operator: 'GT', value: 0 }],
    });
    setFormSeverityRules((rule as any).severity_rules || []);
    setFormEntities((rule as any).applies_to_entities || []);
    setFormFrameworks((rule as any).applies_to_frameworks || []);
    setFormTitle(rule.output_template?.title || '');
    setFormDescTemplate(rule.output_template?.description_template || '');
    setFormAction(rule.output_template?.recommended_action || '');
    setShowBuilder(true);
    setTestResult(null);
  };

  const openNew = () => {
    setEditingId(null);
    setFormName(''); setFormCode(''); setFormDesc('');
    setFormCategory('general_ledger'); setFormExcType('journal_anomaly');
    setFormSeverity('MEDIUM'); setFormPriority(100);
    setFormConditionGroup({ logic: 'AND', rules: [{ field: 'amount', operator: 'GT', value: 0 }] });
    setFormSeverityRules([]); setFormEntities([]); setFormFrameworks([]);
    setFormTitle(''); setFormDescTemplate(''); setFormAction('');
    setShowBuilder(true); setTestResult(null);
  };

  const applyTemplate = (t: RuleTemplate) => {
    setFormCategory(t.category);
    setFormExcType(t.exception_type);
    setFormSeverity(t.severity);
    setFormConditionGroup(t.conditions);
    setFormSeverityRules(t.severity_rules || []);
    setFormTitle(t.output_template?.title || '');
    setFormDescTemplate(t.output_template?.description_template || '');
    setFormAction(t.output_template?.recommended_action || '');
  };

  const handleRevert = (conditions: any) => {
    if (!selectedRule) return;
    authFetch(`${API_V1}/rules/${selectedRule.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...selectedRule, conditions }),
    })
      .then(r => { if (!r.ok) throw new Error(`${r.status}`); return r.json(); })
      .then(() => { setActionMsg('Reverted to previous version'); loadRuleDetail(selectedRule.id); load(); })
      .catch(e => setActionMsg(`Error: ${e.message}`));
  };

  if (loading) return <div style={{ padding: 32 }}><p style={{ color: 'var(--text-secondary)' }}>Loading detection rules...</p></div>;
  if (error) return (
    <div style={{ padding: 32 }}>
      <p style={{ color: 'var(--red)' }}>Error: {error}</p>
      <button onClick={load} style={{ marginTop: 8 }}>Retry</button>
    </div>
  );

  const inputStyle = { padding: '6px 10px', fontSize: 13, border: '1px solid var(--border-default)', borderRadius: 6, background: 'var(--bg-card)', color: 'inherit', width: '100%' };
  const selectStyle = { ...inputStyle, cursor: 'pointer' };
  const btnStyle = { padding: '6px 14px', fontSize: 12, fontWeight: 600, border: '1px solid var(--border-default)', borderRadius: 6, cursor: 'pointer', background: 'transparent', color: 'inherit' };

  return (
    <div style={{ padding: 32, maxWidth: 1000 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 className="page-title" style={{ marginBottom: 0 }}>Detection Rules</h2>
        <button onClick={openNew} style={{ ...btnStyle, background: 'var(--green)', color: 'var(--text-primary)', border: 'none' }}>
          + New Rule
        </button>
      </div>

      {actionMsg && (
        <div style={{ padding: '8px 16px', marginBottom: 16, fontSize: 12, background: 'var(--bg-card)', borderRadius: 6 }}>
          {actionMsg}
          <button onClick={() => setActionMsg('')} style={{ marginLeft: 12, fontSize: 11, cursor: 'pointer', background: 'none', border: 'none', color: 'var(--text-tertiary)' }}>dismiss</button>
        </div>
      )}

      {/* Rules List */}
      {!showBuilder && !selectedRule && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-default)', fontSize: 11, textTransform: 'uppercase' as const, color: 'var(--text-tertiary)' }}>
                <th style={{ padding: '10px 12px', textAlign: 'left' }}>Rule</th>
                <th style={{ padding: '10px 8px', textAlign: 'left' }}>Category</th>
                <th style={{ padding: '10px 8px', textAlign: 'center' }}>Severity</th>
                <th style={{ padding: '10px 8px', textAlign: 'center' }}>Active</th>
                <th style={{ padding: '10px 8px', textAlign: 'center' }}>Priority</th>
                <th style={{ padding: '10px 8px', textAlign: 'right' }}>Last Run</th>
                <th style={{ padding: '10px 12px', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rules.length === 0 ? (
                <tr><td colSpan={7} style={{ padding: 20, textAlign: 'center', color: 'var(--text-tertiary)' }}>No detection rules configured.</td></tr>
              ) : rules.map(r => (
                <tr key={r.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                  <td style={{ padding: '8px 12px' }}>
                    <div style={{ fontWeight: 500 }}>
                      {r.is_system_rule && <span title="System rule" style={{ marginRight: 4 }}>&#128274;</span>}
                      <span style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => loadRuleDetail(r.id)}>{r.rule_name}</span>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{r.rule_code}</div>
                  </td>
                  <td style={{ padding: '8px' }}>{r.category.replace(/_/g, ' ')}</td>
                  <td style={{ padding: '8px', textAlign: 'center' }}>
                    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, color: 'var(--text-primary)', background: SEVERITY_COLORS[r.severity] || 'var(--text-tertiary)' }}>
                      {r.severity}
                    </span>
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center' }}>
                    <span
                      style={{ cursor: 'pointer', fontSize: 16 }}
                      onClick={() => toggleActive(r)}
                      title={r.is_active ? 'Click to deactivate' : 'Click to activate'}
                    >
                      {r.is_active ? '\u2705' : '\u26AA'}
                    </span>
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center' }} className="mono">{r.priority}</td>
                  <td style={{ padding: '8px', textAlign: 'right', fontSize: 11, color: 'var(--text-tertiary)' }}>
                    {r.last_run_at ? new Date(r.last_run_at).toLocaleString() : 'Never'}
                  </td>
                  <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                    <button onClick={() => openEdit(r)} style={{ ...btnStyle, fontSize: 11, padding: '3px 8px', marginRight: 4 }}>Edit</button>
                    <button onClick={() => runRule(r.id)} style={{ ...btnStyle, fontSize: 11, padding: '3px 8px' }}>Run</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Rule Detail */}
      {selectedRule && !showBuilder && (
        <div>
          <button onClick={() => setSelectedRule(null)} style={{ ...btnStyle, marginBottom: 16 }}>Back to list</button>
          <div className="card" style={{ padding: 20, marginBottom: 16 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>{selectedRule.rule_name}</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 13 }}>
              <div><span style={{ color: 'var(--text-tertiary)' }}>Code:</span> {selectedRule.rule_code}</div>
              <div><span style={{ color: 'var(--text-tertiary)' }}>Category:</span> {selectedRule.category}</div>
              <div><span style={{ color: 'var(--text-tertiary)' }}>Type:</span> {selectedRule.exception_type}</div>
              <div><span style={{ color: 'var(--text-tertiary)' }}>Severity:</span> <span style={{ color: SEVERITY_COLORS[selectedRule.severity] }}>{selectedRule.severity}</span></div>
              <div><span style={{ color: 'var(--text-tertiary)' }}>Priority:</span> {selectedRule.priority}</div>
              <div><span style={{ color: 'var(--text-tertiary)' }}>Version:</span> v{selectedRule.version}</div>
              <div><span style={{ color: 'var(--text-tertiary)' }}>Active:</span> {selectedRule.is_active ? 'Yes' : 'No'}</div>
              <div><span style={{ color: 'var(--text-tertiary)' }}>System:</span> {selectedRule.is_system_rule ? 'Yes' : 'No'}</div>
            </div>
            {selectedRule.description && <p style={{ marginTop: 12, fontSize: 13, color: 'var(--text-secondary)' }}>{selectedRule.description}</p>}
          </div>

          <div className="card" style={{ padding: 20, marginBottom: 16 }}>
            <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Conditions</h4>
            <pre style={{ fontSize: 12, whiteSpace: 'pre-wrap', color: 'var(--text-secondary)' }}>
              {JSON.stringify(selectedRule.conditions, null, 2)}
            </pre>
          </div>

          {/* Rule Analytics */}
          <RuleAnalytics runs={selectedRule.recent_runs || []} />

          {/* Version History */}
          <VersionHistory
            ruleId={selectedRule.id}
            currentVersion={selectedRule.version}
            onRevert={handleRevert}
            btnStyle={btnStyle}
          />
        </div>
      )}

      {/* Rule Builder */}
      {showBuilder && (
        <div>
          <button onClick={() => { setShowBuilder(false); setEditingId(null); }} style={{ ...btnStyle, marginBottom: 16 }}>Cancel</button>
          <div className="card" style={{ padding: 20, marginBottom: 16 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>{editingId ? 'Edit Rule' : 'New Rule'}</h3>

            {/* Template gallery — new rules only */}
            {!editingId && (
              <RuleTemplateGallery onSelect={applyTemplate} btnStyle={btnStyle} />
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div>
                <label style={{ fontSize: 11, color: 'var(--text-tertiary)', display: 'block', marginBottom: 4 }}>Rule Name</label>
                <input value={formName} onChange={e => setFormName(e.target.value)} style={inputStyle} placeholder="e.g. High-Value Unmatched Invoices" />
              </div>
              <div>
                <label style={{ fontSize: 11, color: 'var(--text-tertiary)', display: 'block', marginBottom: 4 }}>Rule Code</label>
                <input value={formCode} onChange={e => setFormCode(e.target.value)} style={inputStyle} placeholder="e.g. CUSTOM-AP-001" disabled={!!editingId} />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ fontSize: 11, color: 'var(--text-tertiary)', display: 'block', marginBottom: 4 }}>Description</label>
                <input value={formDesc} onChange={e => setFormDesc(e.target.value)} style={inputStyle} placeholder="Human-readable explanation" />
              </div>
              <div>
                <label style={{ fontSize: 11, color: 'var(--text-tertiary)', display: 'block', marginBottom: 4 }}>Category</label>
                <select value={formCategory} onChange={e => setFormCategory(e.target.value)} style={selectStyle}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 11, color: 'var(--text-tertiary)', display: 'block', marginBottom: 4 }}>Exception Type</label>
                <input value={formExcType} onChange={e => setFormExcType(e.target.value)} style={inputStyle} placeholder="e.g. journal_anomaly" />
              </div>
              <div>
                <label style={{ fontSize: 11, color: 'var(--text-tertiary)', display: 'block', marginBottom: 4 }}>Priority</label>
                <input type="number" value={formPriority} onChange={e => setFormPriority(Number(e.target.value))} style={inputStyle} />
              </div>
            </div>

            {/* Severity + Escalation Rules */}
            <h4 style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Severity</h4>
            <div style={{ marginBottom: 16 }}>
              <SeverityRulesEditor
                defaultSeverity={formSeverity}
                rules={formSeverityRules}
                onDefaultChange={setFormSeverity}
                onRulesChange={setFormSeverityRules}
                fields={FIELDS}
                operators={OPERATORS}
                inputStyle={inputStyle}
                selectStyle={selectStyle}
                btnStyle={btnStyle}
              />
            </div>

            {/* Entity/Framework Scoping */}
            <h4 style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Scoping</h4>
            <div style={{ marginBottom: 16 }}>
              <EntityFrameworkScoping
                entities={formEntities}
                frameworks={formFrameworks}
                availableEntities={availableEntities}
                onEntitiesChange={setFormEntities}
                onFrameworksChange={setFormFrameworks}
              />
            </div>

            {/* Conditions */}
            <h4 style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Conditions</h4>
            <div style={{ marginBottom: 16 }}>
              <ConditionGroupEditor
                group={formConditionGroup}
                onChange={setFormConditionGroup}
                fields={FIELDS}
                operators={OPERATORS}
                inputStyle={inputStyle}
                selectStyle={selectStyle}
                btnStyle={btnStyle}
              />
            </div>

            {/* Output Template */}
            <h4 style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, marginTop: 8 }}>Output Template</h4>
            <div style={{ display: 'grid', gap: 8, marginBottom: 16 }}>
              <div>
                <label style={{ fontSize: 11, color: 'var(--text-tertiary)', display: 'block', marginBottom: 4 }}>Title</label>
                <input value={formTitle} onChange={e => setFormTitle(e.target.value)} style={inputStyle} placeholder="Exception title" />
              </div>
              <div>
                <label style={{ fontSize: 11, color: 'var(--text-tertiary)', display: 'block', marginBottom: 4 }}>Description Template <span style={{ fontSize: 10 }}>(use {'{field}'} placeholders)</span></label>
                <input value={formDescTemplate} onChange={e => setFormDescTemplate(e.target.value)} style={inputStyle} placeholder="e.g. Transaction {document_number} for {currency} {amount}..." />
              </div>
              <div>
                <label style={{ fontSize: 11, color: 'var(--text-tertiary)', display: 'block', marginBottom: 4 }}>Recommended Action</label>
                <input value={formAction} onChange={e => setFormAction(e.target.value)} style={inputStyle} placeholder="What should the reviewer do?" />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={testRuleDry} style={{ ...btnStyle, background: 'var(--amber)', color: '#000', border: 'none' }}>Test Rule</button>
              <button onClick={saveRule} style={{ ...btnStyle, background: 'var(--green)', color: 'var(--text-primary)', border: 'none' }}>
                {editingId ? 'Update Rule' : 'Save Rule'}
              </button>
            </div>

            {testResult && (
              <div style={{ marginTop: 16, padding: 12, background: 'var(--bg-card)', borderRadius: 6 }}>
                <h5 style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Test Results</h5>
                {testResult.error ? (
                  <p style={{ color: 'var(--red)', fontSize: 12 }}>{testResult.error}</p>
                ) : testResult.errors ? (
                  <div style={{ color: 'var(--red)', fontSize: 12 }}>
                    {(Array.isArray(testResult.errors) ? testResult.errors : []).map((e: string, i: number) => <div key={i}>{e}</div>)}
                  </div>
                ) : (
                  <div style={{ fontSize: 12 }}>
                    <div>Sample size: <span className="mono">{testResult.sample_size ?? 0}</span></div>
                    <div>Matches: <span className="mono" style={{ color: (testResult.matches_found ?? 0) > 0 ? 'var(--amber)' : 'var(--green)' }}>{testResult.matches_found ?? 0}</span></div>
                    <div>Match rate: <span className="mono">{testResult.match_rate_pct ?? 0}%</span></div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
